import { useState, useEffect, useRef } from 'react';
import { generateImgproxyUrl } from '../services/imgproxy';
import { config } from '../config/runtimeConfig';

/**
 * Проверка, находится ли приложение в iframe
 */
const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    // Если доступ к window.top запрещён (cross-origin), то мы точно в iframe
    return true;
  }
};

/**
 * Hook для загрузки оптимизированных изображений с lazy loading
 *
 * @param {string} originalPath - путь к оригинальному изображению
 * @param {number} width - ширина изображения
 * @param {number} height - высота изображения
 * @param {Object} options - дополнительные опции
 * @param {number} options.quality - качество изображения (1-100)
 * @param {string} options.format - формат изображения (webp, jpeg, png)
 * @param {boolean} options.lazy - включить lazy loading (default: true, но отключается в iframe/widget mode)
 * @param {string} options.rootMargin - отступ для IntersectionObserver (default: '200px')
 * @returns {Object} - { src, pending, optimizedUrl, isLoading, error, ref }:
 *   `src` — что рисовать в `<img>` (см. ниже), `pending` — подпись ещё в пути.
 */
export const useOptimizedImageStatic = (originalPath, width, height, options = {}) => {
  const { quality = 80, format = 'webp', rootMargin = '200px' } = options;

  // Отключаем lazy loading в widget mode или в iframe, так как IntersectionObserver
  // не работает корректно в nested browsing contexts
  const isWidgetMode = config.ENVIRONMENT_NAME === 'widget';
  const inIframe = isInIframe();
  const shouldDisableLazy = isWidgetMode || inIframe;
  const lazy = options.lazy !== undefined ? options.lazy : !shouldDisableLazy;

  // DEBUG: Log initial state (use console.warn because console.log is stripped in production)
  if (originalPath) {
    console.warn('[useOptimizedImage] Init:', {
      isWidgetMode,
      inIframe,
      shouldDisableLazy,
      lazy,
      envName: config.ENVIRONMENT_NAME,
      path: originalPath?.substring(0, 50)
    });
  }

  const [optimizedUrl, setOptimizedUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(!lazy); // Если lazy=false, сразу видимый
  const containerRef = useRef(null);

  // Если lazy loading отключён (widget mode или iframe), принудительно устанавливаем isVisible = true
  // Это нужно для случая, когда на первом рендере config.ENVIRONMENT_NAME ещё не был 'widget',
  // но на последующих рендерах уже стал 'widget' (race condition)
  useEffect(() => {
    if (!lazy && !isVisible) {
      console.warn('[useOptimizedImage] Forcing isVisible=true because lazy=false');
      setIsVisible(true);
    }
  }, [lazy, isVisible]);

  // IntersectionObserver для lazy loading
  useEffect(() => {
    if (!lazy) return; // Если lazy отключен, пропускаем observer

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect(); // Отключаем observer после первого срабатывания
          }
        });
      },
      {
        root: null, // viewport
        rootMargin, // Загружаем за 200px до входа в viewport
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [lazy, rootMargin]);

  // Загружаем URL когда изображение становится видимым
  useEffect(() => {
    if (!isVisible) return;
    if (!originalPath) {
      // Снимка нет — и превью прежнего снимка показывать нельзя.
      setOptimizedUrl(null);
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setError(null);

    generateImgproxyUrl(originalPath, width, height, { quality, format })
      .then((url) => {
        if (isMounted) {
          if (url) {
            setOptimizedUrl(url);
          } else {
            // Без сброса `src` показал бы превью ПРЕДЫДУЩЕГО снимка (карусель).
            setOptimizedUrl(null);
            setError(new Error('Failed to generate imgproxy URL'));
          }
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setOptimizedUrl(null);
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isVisible, originalPath, width, height, quality, format]);

  // Что рисовать (#1469): подписанное превью, а пока подпись в пути — ничего.
  // Оригинал — только если подписать не вышло (imgproxy недоступен, чужой
  // хост). Раньше карточки рисовали оригинал сразу и меняли его на превью, и
  // браузер качал оба: 160–320 КБ оригинала ради 38 КБ превью на каждой
  // карточке каталога. При перелистывании до прихода новой подписи остаётся
  // предыдущее превью — как и было.
  const pending = isLoading && !!originalPath;
  const src = optimizedUrl || (pending ? null : originalPath || null);

  return {
    src,
    pending,
    optimizedUrl,
    isLoading,
    error,
    ref: containerRef, // Ref для контейнера изображения
  };
};
